import './style.scss';

import type React from 'react';
import { useState } from 'react';

import { getUserData } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { postReconciliationDecision } from '../../../lib/reconciliation-api';
import { TypeButton } from '../../utils/button';
import { Hvc } from '../../utils/hooks';
import { TypeInput } from '../../utils/input';
import { CheckSVG, ErrorSVG, FlagSVG, InfoSVG, StatusSVG, UploadSVG, ViolationSVG } from '../../utils/svgs';
import { TypeTextArea } from '../../utils/text-area';
import SendReportStatusStage from './send-report-status';

const assignmentDetails = [
	{ id: 'wrong-assignment', icon: 'violation', value: 'Wrong assignment' },
	{ id: 'no-assignment', icon: 'error', value: 'No Assignment' },
	{ id: 'result-not-submitting', icon: 'status', value: 'Result not submitting' },
	{ id: 'cannot-upload', icon: 'upload', value: 'Cannot upload result' },
	{ id: 'other', icon: 'flag', value: 'Other' },
];

const issueIcons: Record<string, React.ReactNode> = {
	violation: <ViolationSVG color='var(--primary-700)' />,
	error: <ErrorSVG color='var(--primary-700)' />,
	status: <StatusSVG color='var(--primary-700)' />,
	upload: <UploadSVG color='var(--primary-700)' />,
	flag: <FlagSVG />,
};

const MAX_COMMENT_LENGTH = 500;

const ReportIssueStage = () => {
	const {
		global: {
			state: { reportIssues },
			updateState,
			clearState,
		},
		rsProps,
		actions,
		states,
	} = useGlobalContext();

	const userData = {
		email: getUserData().user?.Email || '',
		phoneNumber: getUserData().user?.PhoneNumber || '',
		userId: getUserData().user?.UserId || '',
	};

	const [isSuccess, setIsSuccess] = useState<boolean>(false);
	// Reveal the custom-title input when the form was opened with a prefilled title
	// that isn't one of the preset issue types (e.g. an escalated reconciliation).
	const [isOtherSelected, setIsOtherSelected] = useState<boolean>(() => {
		const prefilled = reportIssues?.report?.title?.trim();
		return !!prefilled && !assignmentDetails.some((item) => item.value === prefilled);
	});

	const handleSelect = (item: (typeof assignmentDetails)[number]) => () => {
		if (item.id === 'other') {
			setIsOtherSelected(true);
			updateState('reportIssues', {
				...userData,
				report: {
					...reportIssues.report,
					title: '',
				},
			});
		} else {
			setIsOtherSelected(false);
			updateState('reportIssues', {
				...userData,
				report: {
					...reportIssues.report,
					title: item.value,
				},
			});
		}
	};

	const hasTitle = reportIssues.report.title.trim().length > 0;
	const hasBody = reportIssues.report.body.trim().length > 0;
	const isFormValid = hasTitle && hasBody;
	const charCount = reportIssues.report.body.length;

	const onSendReport = () => {
		if (!isFormValid) return;
		// Captured before clearState wipes the slice — used to link the created
		// report back to a reconciliation decision when opened via Escalate.
		const reconContext = reportIssues.reconContext;
		actions?.create_Report({
			data: {
				body: reportIssues.report.body,
				title: reportIssues.report.title,
			},
			onSuccess: (res) => {
				if (reconContext) {
					const body = res as unknown as
						| { id?: string; referenceId?: string; data?: { id?: string; referenceId?: string } }
						| null
						| undefined;
					const reportId = body?.id ?? body?.referenceId ?? body?.data?.id ?? body?.data?.referenceId ?? null;
					void postReconciliationDecision({
						electionId: reconContext.electionId,
						level: reconContext.level,
						entityCode: reconContext.entityCode,
						state: 'escalated',
						note: reconContext.note,
						reportId: reportId ? String(reportId) : null,
					});
				}
				clearState('reportIssues');
				setIsOtherSelected(false);
				setIsSuccess(true);
			},
		});
	};

	const onViewReports = () => {
		rsProps?.callSection({
			action: 'view',
			component: 'report-feedbacks',
			title: 'Report Feedback',
		});
	};

	const onDoneSubmission = () => {
		setIsSuccess(false);
	};

	const isItemSelected = (item: (typeof assignmentDetails)[number]) => {
		if (item.id === 'other') return isOtherSelected;
		return reportIssues.report.title === item.value;
	};

	return (
		<>
			<Hvc
				removeDOM
				view={!isSuccess}
				className='f-column-33'
			>
				<div className='f-row-10 w-100 text-left justify-content-between align-items-center'>
					<h3 className='header-body-text m-0'>Report New Issue</h3>
					<TypeButton
						title='View Feedbacks'
						buttonSize='little'
						buttonType='outlined'
						onClick={onViewReports}
					/>
				</div>
				<div className='f-row-7 align-items-center'>
					<InfoSVG
						width='16'
						height='16'
						color='var(--neutral-500)'
					/>
					<p className='m-0 text-little color-label'>Select an issue type</p>
				</div>
				<div
					className='grid-wrapper-45 gap-10 stagger-children'
					role='radiogroup'
					aria-label='Issue type'
				>
					{assignmentDetails.map((item) => (
						<ReportIssueItem
							key={item.id}
							icon={issueIcons[item.icon]}
							value={item.value}
							isSelected={isItemSelected(item)}
							onClick={handleSelect(item)}
						/>
					))}
				</div>
				<div className={`report-issue-custom ${isOtherSelected ? 'active' : ''}`}>
					<TypeInput
						label='Issue Title'
						placeholder='Describe your issue briefly'
						onChange={(e) => {
							updateState('reportIssues', {
								...userData,
								report: {
									...reportIssues.report,
									title: e.target.value,
								},
							});
						}}
						value={reportIssues.report.title}
					/>
				</div>
				<div className='report-textarea-wrapper'>
					<TypeTextArea
						label='Additional Details'
						placeholder='Provide more context about the issue...'
						onChange={(e) => {
							const value = e.target.value;
							if (value.length > MAX_COMMENT_LENGTH) return;
							updateState('reportIssues', {
								...userData,
								report: {
									...reportIssues.report,
									body: value,
								},
							});
						}}
						value={reportIssues.report.body}
					/>
					<span className={`char-count ${charCount >= MAX_COMMENT_LENGTH ? 'at-limit' : charCount >= 450 ? 'near-limit' : ''}`}>
						{charCount}/{MAX_COMMENT_LENGTH}
					</span>
				</div>
				<div className='f-column-17'>
					<TypeButton
						buttonSize='small'
						title='Submit Report'
						className='w-100'
						buttonType={!isFormValid ? 'disabled' : undefined}
						onClick={onSendReport}
						load={states?._report?.create_ReportLoading}
					/>
				</div>
			</Hvc>
			<Hvc
				removeDOM
				view={isSuccess}
				className='f-column-33 animate-fade-in'
			>
				<SendReportStatusStage onClose={onDoneSubmission} />
			</Hvc>
		</>
	);
};

const ReportIssueItem = ({
	icon,
	value,
	isSelected,
	onClick,
}: {
	icon: React.ReactNode;
	value: string;
	isSelected?: boolean;
	onClick: () => void;
}) => (
	<div
		className={`f-row align-items-center gap-10 report-issue-item ${isSelected ? 'selected' : ''}`}
		onClick={onClick}
		role='radio'
		aria-checked={isSelected}
		tabIndex={0}
		onKeyDown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				onClick();
			}
		}}
	>
		<div className='report-issue-icon'>{icon}</div>
		<p className='m-0 text-little'>{value}</p>
		<div className='report-issue-check'>
			<CheckSVG
				color='var(--accent-500)'
				width='16'
				height='16'
			/>
		</div>
	</div>
);

export default ReportIssueStage;
